// Bump RESOLVER_VERSION whenever this file is modified — survives minification and is
// readable at runtime via window.__mkrResolverVersion for debugging deployed builds.
const RESOLVER_VERSION = 'v12062026';

import jsonLogic, { type RulesLogic } from 'json-logic-js';
import makerVariantRules from '../variants/maker-variant-rules.json';
import makerVariants from '../variants/maker-variants.json';

type VariantRule = {
  condition: RulesLogic;
  variant: string;
};

type AbExperimentBucket = {
  variant: string;
  start: number;
  end: number;
};

type AbExperimentRules = {
  type: 'ab_experiment';
  name: string;
  default: string;
  rolloutPer: number;
  rules: Record<string, unknown>[];
  variants: AbExperimentBucket[];
  startTime?: number;
  endTime?: number;
};

type PageRulesConfig = {
  type?: string;
  default: string;
  rules: VariantRule[];
  startTime?: number;
  endTime?: number;
  rememberVisitors?: boolean;
};

type VariantRulesConfig = AbExperimentRules | PageRulesConfig;

export type Variant = {
  name: string;
  description: string;
  features: Record<string, string | number | boolean>;
};

type OverrideOptionCondition = {
  field: string;
  operator: '==' | 'in' | 'includes';
  values: string[];
};

type OverrideEntry = {
  id: string;
  name: string;
  options: OverrideOptionCondition[];
  overrideValues: Record<string, Record<string, string | number | boolean>>;
};

const variantGroups = makerVariants.variants as Record<string, Variant>;
const variantOverrides: OverrideEntry[] =
  (makerVariants as unknown as { overrides?: OverrideEntry[] }).overrides ?? [];
const variantSelection = makerVariantRules;

export type VariantName = keyof typeof variantGroups;

export type VariantContext = {
  query: Record<string, string>;
  hash_params: Record<string, string>;
  path: string;
  host: string;
  device: 'mobile' | 'desktop';
  browser: string;
  cookies: Record<string, string>;
  js_vars: Record<string, unknown>;
  hour: number;
  // Populated by edge CDN via window.__mkrLocale injected into index.html
  localeCountry: string;
  localeCity: string;
  localeRegion: string;
  localeRegionCode: string;
};

let _currentContext: VariantContext | null = null;

const GA_MEASUREMENT_ID = 'G-Q5L5ML2BV5';
const PROJECT_ID = 'cmkgdrd82009cwf0ybu6o3ras';
const PROJECT_VERSION = '1776159387601';
const EXPERIENCE_STATUS: string = 'live';
const VARIANT_COOKIE_NAME = `__mkr_pr_${PROJECT_ID}`;

const registerCustomOperations = () => {
  jsonLogic.add_operation('js', (code: unknown) => {
    if (typeof code !== 'string' || !code.trim() || !_currentContext) return false;
    try {
      const ctx = _currentContext;
      const fn = new Function(
        'path',
        'host',
        'device',
        'browser',
        'query',
        'hash_params',
        'cookies',
        'js_vars',
        'hour',
        'localeCountry',
        'localeCity',
        'localeRegion',
        'localeRegionCode',
        `const __r = (${code}); return typeof __r === 'function' ? !!(__r()) : !!(__r);`
      );
      return Boolean(
        fn(
          ctx.path,
          ctx.host,
          ctx.device,
          ctx.browser,
          ctx.query,
          ctx.hash_params,
          ctx.cookies,
          ctx.js_vars,
          ctx.hour,
          ctx.localeCountry,
          ctx.localeCity,
          ctx.localeRegion,
          ctx.localeRegionCode
        )
      );
    } catch {
      return false;
    }
  });

  jsonLogic.add_operation(
    'startsWith',
    (str: unknown, prefix: unknown) =>
      typeof str === 'string' && typeof prefix === 'string' && str.startsWith(prefix)
  );

  jsonLogic.add_operation(
    'doesNotStartWith',
    (str: unknown, prefix: unknown) =>
      typeof str === 'string' && typeof prefix === 'string' && !str.startsWith(prefix)
  );

  jsonLogic.add_operation(
    'endsWith',
    (str: unknown, suffix: unknown) =>
      typeof str === 'string' && typeof suffix === 'string' && str.endsWith(suffix)
  );

  jsonLogic.add_operation(
    'doesNotEndWith',
    (str: unknown, suffix: unknown) =>
      typeof str === 'string' && typeof suffix === 'string' && !str.endsWith(suffix)
  );

  jsonLogic.add_operation(
    'includes',
    (str: unknown, substr: unknown) =>
      typeof str === 'string' && typeof substr === 'string' && str.includes(substr)
  );

  jsonLogic.add_operation(
    'doesNotInclude',
    (str: unknown, substr: unknown) =>
      typeof str === 'string' && typeof substr === 'string' && !str.includes(substr)
  );

  jsonLogic.add_operation('matchRegex', (str: unknown, pattern: unknown, flags?: unknown) => {
    if (typeof str !== 'string' || typeof pattern !== 'string') return false;
    try {
      return new RegExp(pattern, typeof flags === 'string' ? flags : undefined).test(str);
    } catch {
      return false;
    }
  });

  jsonLogic.add_operation(
    'in',
    (value: unknown, arr: unknown) => Array.isArray(arr) && arr.includes(value)
  );

  jsonLogic.add_operation(
    'notIn',
    (value: unknown, arr: unknown) => !Array.isArray(arr) || !arr.includes(value)
  );
};

const detectBrowser = (): string => {
  try {
    const ua = navigator.userAgent;
    if (ua.includes('Edg')) return 'Edge';
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Firefox')) return 'Firefox';
    return 'Unknown';
  } catch {
    return 'Unknown';
  }
};

const computeQuery = (): Record<string, string> => {
  try {
    const params = new URLSearchParams(window.location.search);
    const query: Record<string, string> = {};
    params.forEach((value, key) => { query[key] = value; });
    return query;
  } catch {
    return {};
  }
};

const computeDevice = (): 'mobile' | 'desktop' => {
  try {
    return window.innerWidth < 768 ? 'mobile' : 'desktop';
  } catch {
    return 'desktop';
  }
};

const extractVarNames = (node: unknown, prefix: string): string[] => {
  if (!node || typeof node !== 'object') return [];
  if (Array.isArray(node)) return node.flatMap((n) => extractVarNames(n, prefix));

  const record = node as Record<string, unknown>;

  if ('var' in record && typeof record.var === 'string' && record.var.startsWith(prefix)) {
    return [record.var.slice(prefix.length)];
  }

  return Object.values(record).flatMap((v) => extractVarNames(v, prefix));
};

const resolveNestedProperty = (obj: unknown, path: string): unknown => {
  const parts = path.split('.');
  let current = obj;

  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }

  return current;
};

const extractOverrideFieldKeys = (prefix: string): string[] =>
  variantOverrides
    .flatMap((o) => o.options)
    .filter((opt) => opt.field.startsWith(prefix))
    .map((opt) => opt.field.slice(prefix.length));

const resolveField = (field: string, ctx: VariantContext): unknown => {
  if (field.startsWith('query.')) return ctx.query[field.slice(6)];
  if (field.startsWith('hash_params.')) return ctx.hash_params[field.slice(12)];
  if (field.startsWith('cookies.')) return ctx.cookies[field.slice(8)];
  if (field.startsWith('js_vars.')) return resolveNestedProperty(ctx.js_vars, field.slice(8));
  return (ctx as unknown as Record<string, unknown>)[field];
};

const computeHashParams = (): Record<string, string> => {
  try {
    const referencedNames = new Set([
      ...extractVarNames(variantSelection.rules, 'hash_params.'),
      ...extractOverrideFieldKeys('hash_params.')
    ]);
    const params = new URLSearchParams(window.location.hash.slice(1));
    const result: Record<string, string> = {};
    params.forEach((value, key) => {
      if (referencedNames.has(key)) result[key] = value;
    });
    return result;
  } catch {
    return {};
  }
};

const unflattenObject = (entries: [string, unknown][]): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  for (const [path, value] of entries) {
    const parts = path.split('.');
    let current = result;

    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];
      if (!current[part] || typeof current[part] !== 'object') {
        current[part] = {};
      }
      current = current[part] as Record<string, unknown>;
    }

    current[parts[parts.length - 1]] = value;
  }

  return result;
};

const computeJsVars = (): Record<string, unknown> => {
  try {
    const names = [
      ...extractVarNames(variantSelection.rules, 'js_vars.'),
      ...extractOverrideFieldKeys('js_vars.')
    ];
    const entries = names.map((name) => [name, resolveNestedProperty(globalThis, name)]);
    return unflattenObject(entries as [string, unknown][]);
  } catch {
    return {};
  }
};

const computeCookies = (): Record<string, string> => {
  try {
    const referencedNames = new Set([
      ...extractVarNames(variantSelection.rules, 'cookies.'),
      ...extractOverrideFieldKeys('cookies.')
    ]);

    return Object.fromEntries(
      document.cookie
        .split(';')
        .filter((c) => c.trim())
        .map((c) => {
          const [key, ...rest] = c.trim().split('=');
          return [key.trim(), decodeURIComponent(rest.join('='))];
        })
        .filter(([key]) => referencedNames.has(key))
    );
  } catch (err) {
    console.error('[maker-variant-resolver] Failed to parse cookies:', err);
    return {};
  }
};

export const computeContext = (): VariantContext => {
  try {
    return {
      query: computeQuery(),
      hash_params: computeHashParams(),
      path: window.location.pathname,
      host: window.location.host,
      device: computeDevice(),
      browser: detectBrowser(),
      cookies: computeCookies(),
      js_vars: computeJsVars(),
      hour: new Date().getHours(),
      localeCountry: (globalThis as unknown as { __mkrLocale?: { country: string; city: string; region: string; regionCode: string } }).__mkrLocale?.country ?? '<country>',
      localeCity: (globalThis as unknown as { __mkrLocale?: { country: string; city: string; region: string; regionCode: string } }).__mkrLocale?.city ?? '<city>',
      localeRegion: (globalThis as unknown as { __mkrLocale?: { country: string; city: string; region: string; regionCode: string } }).__mkrLocale?.region ?? '<region>',
      localeRegionCode: (globalThis as unknown as { __mkrLocale?: { country: string; city: string; region: string; regionCode: string } }).__mkrLocale?.regionCode ?? '<regionCode>',
    };
  } catch (err) {
    console.error('[maker-variant-resolver] Failed to compute context:', err);
    return {
      query: {}, hash_params: {}, path: '/', host: '',
      device: 'desktop', browser: 'Unknown', cookies: {}, js_vars: {},
      hour: new Date().getHours(), localeCountry: '<country>', localeCity: '<city>', localeRegion: '<region>', localeRegionCode: '<regionCode>',
    };
  }
};

// Preview-only override: not stored in rules JSON, never evaluated in production.
const isPreviewHost = (host: string): boolean => host.includes('-preview.');

// On preview hostnames (e.g. abc123-preview.devi.test), ?mkr_variant=<key> pins the active variant
// so developers can manually switch between variants without editing rules.
const resolvePreviewVariantOverride = (context: VariantContext): VariantName | null => {
  if (!isPreviewHost(context.host)) return null;
  const requested = context.query['mkr_variant'];
  return requested && requested in variantGroups ? (requested as VariantName) : null;
};

export const resolveActiveVariant = (context: VariantContext): VariantName => {
  _currentContext = context;
  try {
    const preview = resolvePreviewVariantOverride(context);
    if (preview) return preview;

    const matchingRule = (variantSelection.rules as VariantRule[]).find((rule) =>
      jsonLogic.apply(rule.condition, context)
    );
    return (matchingRule?.variant ?? variantSelection.default) as VariantName;
  } catch (err) {
    console.error('[maker-variant-resolver] Error resolving variant, falling back to default:', err);
    return variantSelection.default as VariantName;
  } finally {
    _currentContext = null;
  }
};

// TODO: at build time, replace __mkrVariantGroup with __mkrVariantGroup_<projectId> to scope the global per project
type MkrGlobal = { __mkrVariantGroup?: Variant['features'] };

// Per-instance PostHog state set by phReadyScript:
//   undefined = still loading, null = PH disabled, object = ready
type MkrPhState = { distinctId: string; buckets: Record<string, number> } | null;
type MkrPhGlobal = {
  __mkr_ph?: Record<string, MkrPhState | undefined>;
  // Waiters keyed by instanceName that resolve with distinctId once PH fires mkr:ph:ready.
  // Distinct from window.__mkr_ph_waiters (phBucketScript) to avoid collisions.
  __mkr_ph_id_waiters?: Record<string, Promise<string | null>>;
};

const getActiveGroup = (): Variant['features'] =>
  (globalThis as unknown as MkrGlobal).__mkrVariantGroup ?? {};

// FNV-1a 32-bit hash mapped to a 0–9999 bucket. Mirrors computePhPercentageBucket in build.ts.
const computePhBucket = (seed: string, distinctId: string): number => {
  let hash = 0x811c9dc5;
  const combined = `${seed}:${distinctId}`;
  for (let i = 0; i < combined.length; i++) {
    hash ^= combined.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0) % 10000;
};

// Returns a Promise resolving to the 0–9999 bucket for (seed, user).
// Waits for the PostHog instance to fire mkr:ph:ready if not yet loaded.
// Returns null if PostHog is disabled. Buckets are cached on the state object.
// Uses a per-instance distinctId waiter (not per-seed) so concurrent seed requests
// each compute their own bucket once the instance is ready.
export const getPhBucket = (seed: string, instanceName: string): Promise<number | null> => {
  const g = globalThis as unknown as MkrPhGlobal;
  g.__mkr_ph = g.__mkr_ph || {};

  const computeAndCache = (state: Exclude<MkrPhState, null>): number => {
    state.buckets = state.buckets || {};
    if (state.buckets[seed] !== undefined) return state.buckets[seed];
    const bucket = computePhBucket(seed, state.distinctId);
    state.buckets[seed] = bucket;
    return bucket;
  };

  const state = g.__mkr_ph[instanceName];
  if (state !== undefined) {
    return state === null
      ? Promise.resolve(null)
      : Promise.resolve(computeAndCache(state));
  }

  g.__mkr_ph_id_waiters = g.__mkr_ph_id_waiters || {};
  const existing = g.__mkr_ph_id_waiters[instanceName];
  if (existing) {
    return existing.then((distinctId) => {
      if (!distinctId) return null;
      const st = g.__mkr_ph![instanceName];
      return st ? computeAndCache(st) : null;
    });
  }

  const waiter = new Promise<string | null>((resolve) => {
    let settled = false;

    const settle = (value: string | null) => {
      if (settled) return;
      settled = true;
      document.removeEventListener('mkr:ph:ready', handler);
      delete g.__mkr_ph_id_waiters![instanceName];
      resolve(value);
    };

    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ instanceName: string; distinctId: string }>).detail;
      if (detail.instanceName === instanceName) settle(detail.distinctId ?? null);
    };

    document.addEventListener('mkr:ph:ready', handler);
    setTimeout(() => settle(null), 300);
  });

  g.__mkr_ph_id_waiters[instanceName] = waiter;
  return waiter.then((distinctId) => {
    if (!distinctId) return null;
    const st = g.__mkr_ph![instanceName];
    return st ? computeAndCache(st) : null;
  });
};

type ActiveOverride = {
  id: string;
  name: string;
  segments: string[];
};

export type VariantEvent = {
  exp_type: 'ab_experiment' | 'page_rule';
  exp_variant_string: string;
  active_overrides: ActiveOverride[];
};

// Mirrors the matching logic in setActiveVariantGroup to collect which overrides
// fired and which specific option values (segments) matched for each.
const resolveActiveOverrides = (context: VariantContext): ActiveOverride[] =>
  variantOverrides.reduce<ActiveOverride[]>((acc, override) => {
    const segments = override.options.reduce<string[]>((opts, optCond) => {
      const fieldValue = resolveField(optCond.field, context);
      const strValue = fieldValue !== undefined && fieldValue !== null ? String(fieldValue) : '';
      if (optCond.operator === '==' && optCond.values[0] !== undefined && strValue === optCond.values[0]) {
        opts.push(optCond.values[0]);
      } else if (optCond.operator === 'in' && optCond.values.includes(strValue)) {
        opts.push(strValue);
      } else if (optCond.operator === 'includes' && optCond.values[0] !== undefined && strValue.includes(optCond.values[0])) {
        opts.push(optCond.values[0]);
      }
      return opts;
    }, []);
    return segments.length > 0 ? [...acc, { id: override.id, name: override.name, segments }] : acc;
  }, []);

type MkrAnalyticsGlobal = {
  posthog?: { capture?: (event: string, properties: Record<string, unknown>) => void };
  gtag?: (...args: unknown[]) => void;
  dataLayer?: unknown[];
};

// Guards against multiple concurrent poll loops and double logging via dataLayer + gtag
let _gtagEventFired = false;
// Guards mkr_ga_identity to once per session — namespaced by measurement ID to avoid
// collisions when multiple Maker projects are on the same domain
const _gaIdentityKey = `__mkr_ga_identity_sent_${GA_MEASUREMENT_ID}`;

// PostHog queue: accumulates events fired before posthog loads, then replays them.
// null = posthog is ready (or gave up), fire directly; array = still waiting.
let _pendingPhEvents: Array<{ name: string; payload: Record<string, unknown> }> | null = [];

const flushPhQueue = (ph: NonNullable<MkrAnalyticsGlobal['posthog']>): void => {
  const queue = _pendingPhEvents;
  _pendingPhEvents = null;
  queue?.forEach(({ name, payload }) => {
    try {
      if (typeof ph.capture === 'function') ph.capture(name, payload);
    } catch (err) {
      console.error('[maker-variant-resolver] PostHog flush failed:', err);
    }
  });
};

const pollForPosthog = (retry = 0): void => {
  const ph = (globalThis as unknown as MkrAnalyticsGlobal).posthog;
  // Only flush once capture is callable — the stub object appears before the SDK loads
  if (typeof ph?.capture === 'function') { flushPhQueue(ph); return; }
  // Give up after ~3s and discard the queue so nothing fires out of order later
  if (retry < 70) setTimeout(() => pollForPosthog(retry + 1), 42);
  else _pendingPhEvents = null;
};

const capturePhEvent = (name: string, payload: Record<string, unknown>): void => {
  if (_pendingPhEvents !== null) {
    _pendingPhEvents.push({ name, payload });
    // Start the poll loop on the first queued item
    if (_pendingPhEvents.length === 1) pollForPosthog();
    return;
  }
  try {
    const ph = (globalThis as unknown as MkrAnalyticsGlobal).posthog;
    if (typeof ph?.capture === 'function') ph.capture(name, payload);
  } catch (err) {
    console.error('[maker-variant-resolver] PostHog capture failed:', err);
  }
};

const safeSessionStorage = {
  get: (key: string): string | null => {
    try { return sessionStorage.getItem(key); } catch { return null; }
  },
  set: (key: string, value: string): void => {
    try { sessionStorage.setItem(key, value); } catch { /* ignore */ }
  },
};

const readVariantCookie = (): string | null => {
  try {
    const match = document.cookie.match(
      new RegExp('(?:^|; )' + VARIANT_COOKIE_NAME + '=([^;]*)')
    );
    return match ? decodeURIComponent(match[1]) : null;
  } catch { return null; }
};

const writeVariantCookie = (value: string): void => {
  try {
    const expires = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `${VARIANT_COOKIE_NAME}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
  } catch { /* ignore */ }
};

const pollForGtag = (event: VariantEvent, retry = 0): void => {
  const g = globalThis as unknown as MkrAnalyticsGlobal;

  if (_gtagEventFired) return;

  if (!g.gtag) {
    if (retry < 70) setTimeout(() => pollForGtag(event, retry + 1), 42);
    return;
  }

  _gtagEventFired = true;

  if (GA_MEASUREMENT_ID.startsWith('G-')) {
    try {
      // GA4 only accepts scalar params — serialize active_overrides
      g.gtag('event', 'mkr_experience_impression', {
        exp_type: event.exp_type,
        exp_variant_string: event.exp_variant_string,
        active_overrides: JSON.stringify(event.active_overrides),
        non_interaction: true,
        send_to: GA_MEASUREMENT_ID,
      });
    } catch (err) {
      console.error('[maker-variant-resolver] gtag event call failed:', err);
    }

    // Identity mapping: fire once per session so PostHog events can be joined to GA4 records
    if (!safeSessionStorage.get(_gaIdentityKey)) {
      try {
        g.gtag('get', GA_MEASUREMENT_ID, 'client_id', (clientId: unknown) => {
          if (typeof clientId === 'string' && clientId) {
            capturePhEvent('mkr_ga_identity', { ga_client_id: clientId });
            safeSessionStorage.set(_gaIdentityKey, '1');
          }
        });
      } catch (err) {
        console.error('[maker-variant-resolver] gtag get client_id failed:', err);
      }
    }
  }
};

const writeVariantEvent = (event: VariantEvent): void => {
  const g = globalThis as unknown as MkrAnalyticsGlobal;
  const payload = { ...(event as unknown as Record<string, unknown>), non_interaction: true };

  capturePhEvent('mkr_experience_impression', payload);

  // dataLayer fallback — only when gtag absent at call time AND poll hasn't already
  // fired the gtag path. Prevents double logging if gtag loads shortly after.
  if (!g.gtag && !_gtagEventFired && g.dataLayer) {
    try {
      (g.dataLayer as Record<string, unknown>[]).push({
        event: 'mkr_experience_impression',
        exp_type: event.exp_type,
        exp_variant_string: event.exp_variant_string,
        active_overrides: JSON.stringify(event.active_overrides),
        non_interaction: true,
      });
      _gtagEventFired = true;
    } catch (err) {
      console.error('[maker-variant-resolver] dataLayer push failed:', err);
    }
  }

  pollForGtag(event);
};

export const buildVariantEvent = (
  type: 'ab_experiment' | 'page_rule',
  variant: string,
  context: VariantContext,
): VariantEvent => ({
  exp_type: type,
  exp_variant_string: `${PROJECT_ID}-${PROJECT_VERSION}-${variant}`,
  active_overrides: resolveActiveOverrides(context),
});

export const buildAndWriteVariantEvent = (
  type: 'ab_experiment' | 'page_rule',
  variant: string,
  context: VariantContext,
): VariantEvent => {
  const event = buildVariantEvent(type, variant, context);
  writeVariantEvent(event);
  return event;
};

export const resolveAbExperimentVariant = async (
  exp: AbExperimentRules,
  instanceName: string
): Promise<VariantName | null> => {
  const rolloutThreshold = exp.rolloutPer * 100;
  const bucket = await getPhBucket(exp.name, instanceName);
  if (bucket === null || bucket >= rolloutThreshold) return null;
  const match = exp.variants.find((v) => bucket >= v.start && bucket <= v.end);
  if (!match) return null;
  return match.variant as VariantName;
};

// Parses the Dynamic content "Preview" tab's `?mkr_preview_overrides=<id>:<value>|<id2>:<value2>`
// param (id/value URI-encoded) into (override id, segment value) pairs. Malformed pairs are dropped.
const parsePreviewOverridesParam = (param: string | undefined): Array<{ id: string; value: string }> => {
  if (!param) return [];
  return param
    .split('|')
    .map((pair) => {
      const sepIndex = pair.indexOf(':');
      if (sepIndex === -1) return null;
      try {
        const id = decodeURIComponent(pair.slice(0, sepIndex));
        const value = decodeURIComponent(pair.slice(sepIndex + 1));
        return id && value ? { id, value } : null;
      } catch {
        return null;
      }
    })
    .filter((selection): selection is { id: string; value: string } => selection !== null);
};

// On preview hostnames, dynamic-content overrides are hidden by default — e.g. an "hour of day"
// override matching the visitor's real-world context would otherwise corrupt the base preview.
// Pass `?mkr_overrides=1` to opt back in. The flag has no effect outside of preview hostnames.
//
// `?mkr_preview_overrides=<id>:<value>|...` (Dynamic content "Preview" tab) force-applies specific
// override segments regardless of context matching, stacked on top of the variant's features —
// independent of `mkr_overrides`.
const setActiveVariantGroup = (variantName: VariantName, context: VariantContext | null = null) => {
  try {
    const group = variantGroups[variantName];
    if (!group) {
      console.warn(
        `[maker] Variant "${variantName}" not found in maker-variants.json. Falling back to empty features.`
      );
    }
    const forcedSelections =
      context && isPreviewHost(context.host)
        ? parsePreviewOverridesParam(context.query['mkr_preview_overrides'])
        : [];
    const skipOverrides =
      !!context && isPreviewHost(context.host) && context.query['mkr_overrides'] !== '1';
    const merged =
      forcedSelections.length > 0
        ? forcedSelections.reduce<Record<string, string | number | boolean>>((acc, { id, value }) => {
            const override = variantOverrides.find((o) => o.id === id);
            const vars = override?.overrideValues[value];
            return vars ? { ...acc, ...vars } : acc;
          }, group?.features ?? {})
        : skipOverrides
          ? (group?.features ?? {})
          : variantOverrides.reduce<Record<string, string | number | boolean>>(
              (acc, override) => {
                const activeOptions = override.options.reduce<string[]>((opts, optCond) => {
                  const fieldValue = context ? resolveField(optCond.field, context) : undefined;
                  const strValue = fieldValue !== undefined && fieldValue !== null ? String(fieldValue) : '';
                  if (optCond.operator === '==' && optCond.values[0] !== undefined && strValue === optCond.values[0]) {
                    opts.push(optCond.values[0]);
                  } else if (optCond.operator === 'in' && optCond.values.includes(strValue)) {
                    opts.push(strValue);
                  } else if (optCond.operator === 'includes' && optCond.values[0] !== undefined && strValue.includes(optCond.values[0])) {
                    opts.push(optCond.values[0]);
                  }
                  return opts;
                }, []);
                return activeOptions.reduce((innerAcc, opt) => {
                  const vars = override.overrideValues[opt];
                  return vars ? { ...innerAcc, ...vars } : innerAcc;
                }, acc);
              },
              group?.features ?? {}
            );
    (globalThis as unknown as MkrGlobal).__mkrVariantGroup = context
      ? { localeCountry: context.localeCountry, localeCity: context.localeCity, localeRegion: context.localeRegion, localeRegionCode: context.localeRegionCode, ...merged }
      : merged;
    (globalThis as Record<string, unknown>)['__mkrActiveVariantKey'] = variantName;
  } catch (err) {
    console.error('[maker-variant-resolver] setActiveVariantGroup failed:', err);
  }
};

const checkAbExperimentEligibility = (rules: AbExperimentRules, context: VariantContext): boolean => {
  _currentContext = context;
  try {
    return (
      rules.rules.length === 0 ||
      rules.rules.every((rule) => Boolean(jsonLogic.apply(rule as RulesLogic, context)))
    );
  } catch {
    return false;
  } finally {
    _currentContext = null;
  }
};

const resolvePhInstanceAndApply = (rules: AbExperimentRules, context: VariantContext): Promise<void> => {
  const applyVariant = (instanceName: string): Promise<void> =>
    resolveAbExperimentVariant(rules, instanceName).then((variant) => {
      const resolved = (variant ?? rules.default) as VariantName;
      setActiveVariantGroup(resolved, context);
      buildAndWriteVariantEvent('ab_experiment', resolved, context);
    });

  const ph = (globalThis as unknown as MkrPhGlobal).__mkr_ph;
  const readyEntry = ph && Object.entries(ph).find(([, s]) => s !== undefined && s !== null);
  if (readyEntry) {
    return applyVariant(readyEntry[0]).catch((err) => {
      console.error('[maker-variant-resolver] AB experiment variant application failed:', err);
    });
  }

  return new Promise<void>((resolve) => {
    let settled = false;

    const settle = (work: () => Promise<void> | void) => {
      if (settled) return;
      settled = true;
      document.removeEventListener('mkr:ph:ready', handler);
      Promise.resolve(work()).then(resolve).catch((err) => {
        console.error('[maker-variant-resolver] AB experiment variant application failed:', err);
        resolve();
      });
    };

    const handler = (e: Event) => {
      const { instanceName } = (e as CustomEvent<{ instanceName: string }>).detail;
      settle(() => applyVariant(instanceName));
    };

    document.addEventListener('mkr:ph:ready', handler);

    let attempts = 0;
    const poll = () => {
      const g = globalThis as unknown as MkrPhGlobal;
      const entry = g.__mkr_ph && Object.entries(g.__mkr_ph).find(([, s]) => s !== undefined && s !== null);
      if (entry) {
        settle(() => applyVariant(entry[0]));
        return;
      }
      if (++attempts < 6) {
        setTimeout(poll, 50);
      } else {
        settle(() => {
          console.log('[maker] PostHog not ready within 300ms — rendering default variant.');
        });
      }
    };
    setTimeout(poll, 50);
  });
};

const checkTimeWindow = (rules: { startTime?: number; endTime?: number }): boolean => {
  const now = Date.now();
  if (rules.startTime && now < rules.startTime) return false;
  if (rules.endTime && now > rules.endTime) return false;
  return true;
};

export const initializeAbExperimentVariantResolver = (): Promise<void> => {
  try {
    const rules = variantSelection as unknown as AbExperimentRules;
    const context = computeContext();

    const preview = resolvePreviewVariantOverride(context);
    if (preview) {
      setActiveVariantGroup(preview, context);
      return Promise.resolve();
    }

    setActiveVariantGroup(rules.default as VariantName, context);

    if (!checkTimeWindow(rules) || EXPERIENCE_STATUS !== 'live') return Promise.resolve();
    if (!checkAbExperimentEligibility(rules, context)) return Promise.resolve();

    return resolvePhInstanceAndApply(rules, context).catch((err) => {
      console.error('[maker-variant-resolver] AB experiment resolution failed, keeping default variant:', err);
    });
  } catch (err) {
    console.error('[maker-variant-resolver] AB experiment initialization failed, applying default variant:', err);
    try {
      setActiveVariantGroup((variantSelection as unknown as AbExperimentRules).default as VariantName);
    } catch {}
    return Promise.resolve();
  }
};

export const initializePageRuleVariantResolver = () => {
  try {
    const context = computeContext();
    const rules = variantSelection as unknown as PageRulesConfig;

    const preview = resolvePreviewVariantOverride(context);
    if (preview) {
      setActiveVariantGroup(preview, context);
      return;
    }

    if (!checkTimeWindow(rules) || EXPERIENCE_STATUS !== 'live') {
      setActiveVariantGroup(rules.default as VariantName, context);
      return;
    }

    const noSticky = 'mkr_no_sticky' in context.query;

    if (rules.rememberVisitors !== false && !noSticky) {
      const stored = readVariantCookie();
      if (stored && stored in variantGroups) {
        setActiveVariantGroup(stored as VariantName, context);
        buildAndWriteVariantEvent('page_rule', stored, context);
        return;
      }
    }

    const resolved = resolveActiveVariant(context);
    if (rules.rememberVisitors !== false && !noSticky) writeVariantCookie(resolved);
    setActiveVariantGroup(resolved, context);
    buildAndWriteVariantEvent('page_rule', resolved, context);
  } catch (err) {
    console.error('[maker-variant-resolver] Page rule initialization failed, applying default variant:', err);
    try {
      setActiveVariantGroup((variantSelection as unknown as PageRulesConfig).default as VariantName);
    } catch {}
  }
};

export const initializeMakerVariantResolver = (): Promise<void> => {
  try {
    const rules = variantSelection as unknown as VariantRulesConfig;
    if (rules.type === 'ab_experiment') {
      return initializeAbExperimentVariantResolver();
    }
    initializePageRuleVariantResolver();
    return Promise.resolve();
  } catch (err) {
    console.error('[maker-variant-resolver] Variant resolver initialization failed:', err);
    return Promise.resolve();
  }
};

export const makerVariant = <T,>(key: string, fallback?: T): T => {
  const value = getActiveGroup()[key];

  return value !== undefined ? (value as T) : (fallback as T);
};

export const makerVariantComponent = <T,>(
  key: string,
  registry: Record<string, T>,
  fallback: T
): T => {
  const name = makerVariant<string>(key);

  return registry[name] ?? fallback;
};

export const makerVariantReady = (): Promise<void> =>
  ((globalThis as Record<string, unknown>)['__mkrVariantReady'] as Promise<void>) ?? Promise.resolve();

if (!(globalThis as Record<string, unknown>)['__mkrVariantResolverInitialized']) {
  (globalThis as Record<string, unknown>)['__mkrVariantResolverInitialized'] = true;
  (globalThis as Record<string, unknown>)['__mkrResolverVersion'] = RESOLVER_VERSION;
  try {
    registerCustomOperations();
  } catch (err) {
    console.error('[maker-variant-resolver] Failed to register custom operations:', err);
  }
  (globalThis as Record<string, unknown>)['__mkrVariantReady'] = initializeMakerVariantResolver();
}